// Sanity checks run on a template-replayed header (see parseUsingTemplate)
// before it's shown to staff. A stale/mis-anchored template can silently keep
// reading the wrong cell (e.g. the "Invoice No:" label itself instead of the
// value next to it) forever -- these catch the common failure shapes so the
// hybrid parser can fall back to a targeted single-field AI read instead of
// shipping obviously-wrong data.

const LABEL_LEAK_PREFIX = /^(invoice|no|ref)\b/i

export function isValidInvoiceNo(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const v = value.trim()
  if (v.length < 5 || v.length > 30) return false
  if (!/[a-zA-Z0-9]/.test(v)) return false // not just punctuation like '*' or ':'
  if (LABEL_LEAK_PREFIX.test(v)) return false
  return true
}

export function isValidInvoiceDate(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) return false
  return !isNaN(new Date(value).getTime())
}

export function isValidTotalAmount(value: unknown): boolean {
  return typeof value === 'number' && isFinite(value) && value > 0
}

export function isValidCurrency(value: unknown): boolean {
  return typeof value === 'string' && /^[A-Za-z]{3}$/.test(value.trim())
}

export function isValidPartyName(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const v = value.trim()
  if (v.length <= 3) return false
  if (!/[a-zA-Z0-9]/.test(v)) return false // not just punctuation
  return true
}

// Keyed by InvoiceHeader field name, not a separate "invoice_no"/"shipper_name"
// vocabulary -- shipper/consignee are the actual header fields.
export const HEADER_FIELD_VALIDATORS: Record<string, (value: unknown) => boolean> = {
  invoice_no: isValidInvoiceNo,
  invoice_date: isValidInvoiceDate,
  total_amount: isValidTotalAmount,
  currency: isValidCurrency,
  shipper: isValidPartyName,
  consignee: isValidPartyName,
}

export function findInvalidHeaderFields(header: Record<string, unknown>): string[] {
  return Object.entries(HEADER_FIELD_VALIDATORS)
    .filter(([field, validate]) => !validate(header[field]))
    .map(([field]) => field)
}
